import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Form, Radio, Space } from 'antd'
import { Button, BUTTON_TYPES } from '../../../../../../components/Button'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { Banner } from '../../../../../../components/Banner/Banner'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Input } from 'components/Input'
import { Select } from '../../../../../../components/Select'
import { RowLayout } from './RowLayout'
import { formatPhoneNumber, validateOnCondition } from 'helper/common'
import { useDebounce } from 'hooks/hooks'
import {
  checkVendorAlternateName,
  checkVendorLegalName,
  checkVendorName
} from '../../api'
import { fetchVendorOptions } from '../../../../../PaymentDetails/api'
import { findDuplicateIndices } from './utils'
import VendorAddress from './VendorAddress'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Delete24 } from 'assets/svg/Delete16Red.svg'
import { ReactComponent as InformationIcon } from 'assets/svg/Information.svg'
import {
  BTN_TXT,
  LABELS,
  PLACEHOLDERS,
  TOOLTIP,
  VALIDATION_MESSAGES
} from 'constants/txt'
import { VendorFormProps } from './types'
import { TOption } from '../../../../../../components/Select/types'
import './styles.scss'
import { useSelector } from 'react-redux'
import { getVendorInfo } from '../../../../../../redux/store/vendorInfo/getters'
import { ability, ACTIONS, SUBJECTS } from '../../../../../Permission'
import { COUNTRIES_LIST } from '../../constants'

const VendorForm = ({
  form,
  phoneNumber,
  disabled,
  getVendorError,
  isCreation,
  disableNamesSearch = false,
  businessDetails
}: VendorFormProps) => {
  const fetchedVendorInfo = useSelector(getVendorInfo)
  const vendorBusinessName = Form.useWatch(['vendor', 'business_name'], form)
  const vendorPermission = Form.useWatch(['vendor', 'vendor_permission'], form)
  const vendorLegalName = Form.useWatch(['vendor', 'name'], form)
  const remitToAddresses = Form.useWatch(['remit_to_address_set'], form)
  const orderFromAddress = Form.useWatch(['order_from_address_set'], form)
  const alternateNames = Form.useWatch('alternate_name_set', form)
  const [businessTypeList, setBusinessTypeList] = useState<TOption[]>([])

  const canCreateOnlyGuestAccess = ability.can(
    ACTIONS.CREATE,
    SUBJECTS.ONLY_GUEST_VENDORS
  )

  const [alternateNameValidationResults, setAlternateNameValidationResults] =
    useState(
      new Map<string, { is_identical_name_exists: boolean; values: string[] }>()
    )
  const [businessNameValidationResult, setBusinessNameValidationResult] =
    useState<{ is_identical_name_exists: boolean; values: string[] } | null>(
      null
    )
  const [legalNameValidationResult, setLegalNameValidationResult] = useState<{
    is_identical_name_exists: boolean
    values: string[]
  } | null>(null)

  const debouncedSearchBusinessName = useDebounce(vendorBusinessName, 500)
  const debouncedSearchLegalName = useDebounce(vendorLegalName, 500)

  const handleVendorBusinessName = useCallback(async () => {
    if (
      vendorBusinessName?.trim() === fetchedVendorInfo?.business_name?.trim()
    ) {
      setBusinessNameValidationResult(null)
      return
    }
    const resp = await checkVendorName({ business_name: vendorBusinessName })
    if (resp?.data) {
      setBusinessNameValidationResult({
        is_identical_name_exists: resp.data.is_identical_name_exists,
        values: resp.data.values || []
      })
    }
  }, [fetchedVendorInfo?.business_name, vendorBusinessName])

  const isLegalNameSynced = useMemo(() => {
    return (
      !!vendorLegalName &&
      businessDetails?.legal_business_name === vendorLegalName
    )
  }, [businessDetails, vendorLegalName])

  const handleLegalBusinessName = useCallback(async () => {
    if (vendorLegalName?.trim() === fetchedVendorInfo?.name?.trim()) {
      setLegalNameValidationResult(null)
      return
    }
    const resp = await checkVendorLegalName({ name: vendorLegalName })
    if (resp?.data) {
      setLegalNameValidationResult({
        is_identical_name_exists: resp.data.is_identical_name_exists,
        values: resp.data.values || []
      })
    }
  }, [vendorLegalName, fetchedVendorInfo?.name])

  useEffect(() => {
    if (debouncedSearchBusinessName?.length >= 4) handleVendorBusinessName()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchBusinessName])

  useEffect(() => {
    if (debouncedSearchLegalName?.length >= 4) handleLegalBusinessName()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchLegalName])

  const duplicateIndicesRemit = useMemo(() => {
    if (!remitToAddresses?.[0]?.state) return new Set()
    return findDuplicateIndices(remitToAddresses)
  }, [remitToAddresses])
  const duplicateIndicesOrder = useMemo(() => {
    if (!orderFromAddress?.[0]?.state) return new Set()
    return findDuplicateIndices(orderFromAddress)
  }, [orderFromAddress])

  const duplicateAlternateIndices = useMemo(() => {
    if (!alternateNames) return new Set()
    const seen = new Map()
    const duplicates = new Set()

    const allVendorsNames = [
      ...alternateNames,
      { name: vendorBusinessName },
      { name: vendorLegalName }
    ]
    allVendorsNames.forEach((item, index) => {
      const key = item?.name?.trim().toLowerCase()
      if (!key) return
      if (seen.has(key)) {
        duplicates.add(index)
        duplicates.add(seen.get(key))
      } else {
        seen.set(key, index)
      }
    })

    return duplicates
  }, [alternateNames, vendorBusinessName, vendorLegalName])

  const isBusinessNameDuplicate = useMemo(() => {
    if (!vendorBusinessName?.trim()) return false
    const businessNameKey = vendorBusinessName.trim().toLowerCase()

    if (alternateNames?.length) {
      return alternateNames.some(
        (alt) => alt?.name?.trim().toLowerCase() === businessNameKey
      )
    }

    return false
  }, [vendorBusinessName, alternateNames])

  const isLegalNameDuplicate = useMemo(() => {
    if (!vendorLegalName?.trim()) return false
    const legalNameKey = vendorLegalName.trim().toLowerCase()

    if (alternateNames?.length) {
      return alternateNames.some(
        (alt) => alt?.name?.trim().toLowerCase() === legalNameKey
      )
    }

    return false
  }, [vendorLegalName, alternateNames])

  const debounceTimers = useRef({})

  useEffect(() => {
    if (!vendorBusinessName?.length) {
      setBusinessNameValidationResult(null)
    }
  }, [vendorBusinessName])
  useEffect(() => {
    if (!vendorLegalName?.length) {
      setLegalNameValidationResult(null)
    }
  }, [vendorLegalName])

  const handleNameChange = (idx, value) => {
    if (value.length <= 3) return
    const trimmed = value?.trim().toLowerCase()
    if (!trimmed || disableNamesSearch) return
    if (debounceTimers.current[idx]) {
      clearTimeout(debounceTimers.current[idx])
    }

    debounceTimers.current[idx] = setTimeout(async () => {
      try {
        const resp = await checkVendorAlternateName({ name: value })

        setAlternateNameValidationResults((prev) => {
          const newMap = new Map(prev)
          if (resp?.data) {
            newMap.set(value, {
              is_identical_name_exists: resp.data.is_identical_name_exists,
              values: resp.data.values || []
            })
          }
          const currentNamesSet = new Set(
            [...alternateNames, { name: value }]
              .map((n) => n.name?.trim().toLowerCase())
              .filter(Boolean)
          )
          return new Map(
            Array.from(newMap.entries()).filter(([key]) =>
              currentNamesSet.has(key.trim().toLowerCase())
            )
          )
        })
      } catch (e) {
        console.error(e)
      }
    }, 400)
  }

  const getBusinessTypeList = async () => {
    await fetchVendorOptions().then((res) => {
      setBusinessTypeList(
        res.data.results.map((option) => ({
          label: option.value,
          value: option.id
        }))
      )
    })
  }

  useEffect(() => {
    getBusinessTypeList()
  }, [])

  const getValidationStatus = useCallback(
    (error?: string) => (error ? 'error' : undefined),
    []
  )

  const getBusinessNameHelpMessage = useCallback(
    (hasIdenticalName: boolean, hasSimilarNames: boolean) => {
      if (hasIdenticalName) {
        return VALIDATION_MESSAGES.VENDOR_BUSINESS_NAME_EXCITED
      }

      if (isBusinessNameDuplicate || hasSimilarNames) {
        return VALIDATION_MESSAGES.VENDOR_NAME_DUPLICATE
      }

      return getVendorError(['business_name'])
    },
    [getVendorError, isBusinessNameDuplicate]
  )
  const NameField = useMemo(() => {
    const hasIdenticalName =
      !!businessNameValidationResult?.is_identical_name_exists
    const hasSimilarNames =
      !!businessNameValidationResult?.values?.length &&
      !hasIdenticalName &&
      !isCreation

    const helpMessage = getBusinessNameHelpMessage(
      hasIdenticalName,
      hasSimilarNames
    )

    return (
      <Input
        propsItem={{
          name: ['vendor', 'business_name'],
          label: 'Vendor name (doing business as)',
          help: helpMessage,
          validateStatus:
            getValidationStatus(getVendorError(['business_name'])) ||
            (hasIdenticalName || isBusinessNameDuplicate
              ? 'error'
              : hasSimilarNames
              ? 'warning'
              : undefined),
          rules: [
            {
              max: 64,
              required: !disabled,
              validator: () =>
                validateOnCondition(hasIdenticalName || isBusinessNameDuplicate)
            }
          ]
        }}
        propsInput={{
          disabled,
          showCount: true,
          maxLength: 64
        }}
        warningPopover={{
          showPopup: hasSimilarNames && isCreation && !disabled,
          title: 'Vendors with a similar name in the system:',
          items: hasSimilarNames
            ? businessNameValidationResult?.values
            : undefined
        }}
      />
    )
  }, [
    businessNameValidationResult?.is_identical_name_exists,
    businessNameValidationResult?.values,
    getBusinessNameHelpMessage,
    getValidationStatus,
    getVendorError,
    isBusinessNameDuplicate,
    disabled,
    isCreation
  ])

  const getNameHelpMessage = useCallback(
    (hasIdenticalName: boolean, hasSimilarNames: boolean) => {
      if (hasIdenticalName) {
        return VALIDATION_MESSAGES.VENDOR_BUSINESS_NAME_EXCITED
      }

      if (isLegalNameDuplicate || hasSimilarNames) {
        return VALIDATION_MESSAGES.VENDOR_NAME_DUPLICATE
      }

      return getVendorError(['name'])
    },
    [getVendorError, isLegalNameDuplicate]
  )
  const LegalNameField = useMemo(() => {
    const hasIdenticalName =
      !!legalNameValidationResult?.is_identical_name_exists
    const hasSimilarNames =
      !!legalNameValidationResult?.values?.length &&
      !hasIdenticalName &&
      isCreation

    const helpMessage = getNameHelpMessage(hasIdenticalName, hasSimilarNames)
    return (
      <Input
        propsItem={{
          name: ['vendor', 'name'],
          label: (
            <div className="row space-between">
              {LABELS.VENDOR_LEGAL_NAME}&nbsp;
              <Tooltip
                title={
                  isLegalNameSynced
                    ? TOOLTIP.SYNCED_WITH_LEGAL
                    : TOOLTIP.NOT_SYNCED_WITH_LEGAL
                }
              >
                <div
                  className={cn('vendor-indicator', {
                    'gray-indicator': !isLegalNameSynced,
                    'green-indicator': isLegalNameSynced
                  })}
                />
              </Tooltip>
              &nbsp;
            </div>
          ),
          help: helpMessage,
          validateStatus:
            getValidationStatus(getVendorError(['name'])) ||
            (hasIdenticalName || isLegalNameDuplicate
              ? 'error'
              : hasSimilarNames
              ? 'warning'
              : undefined),
          rules: [
            {
              max: 64,
              validator: () =>
                validateOnCondition(hasIdenticalName || isLegalNameDuplicate)
            }
          ]
        }}
        propsInput={{
          disabled,
          showCount: true,
          maxLength: 64
        }}
        warningPopover={{
          showPopup: hasSimilarNames && isCreation,
          title: 'Vendors with a similar name in the system:',
          items: hasSimilarNames ? legalNameValidationResult?.values : undefined
        }}
      />
    )
  }, [
    disabled,
    getNameHelpMessage,
    getValidationStatus,
    getVendorError,
    isCreation,
    isLegalNameDuplicate,
    isLegalNameSynced,
    legalNameValidationResult?.is_identical_name_exists,
    legalNameValidationResult?.values
  ])

  const VendorPermissionsField = useMemo(
    () => (
      <div
        className={cn('column align-start vendor-permissions-wrapper', {
          view: disabled
        })}
      >
        <Form.Item
          label="Vendor permission"
          className={cn({ 'form-item-required': isCreation })}
          name={['vendor', 'vendor_permission']}
        >
          {!disabled && isCreation ? (
            <Radio.Group className="column" disabled={disabled || !isCreation}>
              <Radio value="full_access" disabled={canCreateOnlyGuestAccess}>
                <Typography.Body1>Full access</Typography.Body1>
              </Radio>
              <Radio value="guest" className="mt-8">
                <Typography.Body1 className="guest-radio-label">
                  Guest{' '}
                  <span>(access only for Product Requests functionality)</span>
                </Typography.Body1>
              </Radio>
            </Radio.Group>
          ) : (
            <>
              {vendorPermission === 'guest' ? (
                <Typography.Body1>
                  Guest{' '}
                  <span>(access only for Product Requests functionality)</span>
                </Typography.Body1>
              ) : (
                <Typography.Body1>Full access</Typography.Body1>
              )}
            </>
          )}
        </Form.Item>
      </div>
    ),
    [disabled, isCreation, canCreateOnlyGuestAccess, vendorPermission]
  )

  const DescriptionField = useMemo(
    () => (
      <Input.TextArea
        propsItem={{
          name: ['vendor', 'description'],
          label: 'Description',
          help: getVendorError(['description']),
          validateStatus: getValidationStatus(getVendorError(['description'])),
          rules: [
            {
              max: 250
            }
          ]
        }}
        propsTextArea={{
          disabled,
          showCount: true,
          maxLength: 250
        }}
      />
    ),
    [disabled, getValidationStatus, getVendorError]
  )

  const PhoneNumberField = useMemo(
    () => (
      <Input.Phone
        propsItem={{
          name: ['vendor', 'phone_number'],
          label: 'Vendor’s phone number',
          help: getVendorError(['phone_number']),
          validateStatus: getValidationStatus(getVendorError(['phone_number'])),
          rules: [
            {
              pattern: /^[\d]{10,15}$/,
              validateTrigger:
                phoneNumber?.length === 10 ? ['onSubmit'] : ['onBlur']
            }
          ]
        }}
        propsInputNumber={{
          disabled,
          controls: false,
          formatter: formatPhoneNumber,
          prefix: phoneNumber ? '+1' : '',
          placeholder: PLACEHOLDERS.PHONE_NUMBER
        }}
      />
    ),
    [disabled, getValidationStatus, getVendorError, phoneNumber]
  )

  const BusinessTypeField = useMemo(
    () => (
      <Select
        options={businessTypeList}
        propsItem={{
          label: (
            <div className="row">
              {LABELS.BUSINESS_TYPE}&nbsp;
              <Tooltip title={LABELS.FEDERAL_TAX_CLASSIFICATION}>
                <InformationIcon />
              </Tooltip>
              &nbsp;
            </div>
          ),
          name: ['vendor', 'business_type'],
          help: getVendorError(['vendor', 'business_type']),
          validateStatus: getValidationStatus(
            getVendorError(['vendor', 'business_type'])
          )
        }}
        propsSelect={{
          placeholder: PLACEHOLDERS.PLEASE_SELECT,
          disabled,
          allowClear: true
        }}
      />
    ),
    [disabled, getValidationStatus, getVendorError, businessTypeList]
  )

  const returnAlternateName = (key, name, remove, isDuplicate) => {
    const validationResult = alternateNameValidationResults.get(
      alternateNames?.[key]?.name
    )
    const hasIdenticalName =
      validationResult?.is_identical_name_exists || isDuplicate
    const hasSimilarNames =
      !!validationResult?.values?.length && !hasIdenticalName

    const helpMessage = hasIdenticalName
      ? ''
      : isDuplicate || hasSimilarNames
      ? VALIDATION_MESSAGES.VENDOR_NAME_DUPLICATE
      : getVendorError(['alternate_name_set'])?.[name]

    return (
      <Space
        key={key}
        className="user-form_input-wide hospitals-wrapper full-width alternate-name mt-8"
        direction="vertical"
      >
        <div className={cn('row gap-8 align-start full-width')}>
          <Input
            className="full-width"
            propsItem={{
              name: [name, 'name'],
              label: '',
              validateStatus:
                hasIdenticalName ||
                getVendorError(['alternate_name_set'])?.[name]?.length
                  ? 'error'
                  : hasSimilarNames
                  ? 'warning'
                  : undefined,
              help: helpMessage,
              rules: [
                {
                  max: 64,
                  // reject form submit if we have any duplicates in alternate name set or identical name exists
                  validator: () =>
                    validateOnCondition(isDuplicate || hasIdenticalName)
                }
              ]
            }}
            propsInput={{
              disabled,
              maxLength: 64,
              onChange: (e) => handleNameChange(key, e.target.value)
            }}
            warningPopover={{
              showPopup: hasSimilarNames && isCreation,
              title: 'Vendors with a similar name in the system:',
              items: hasSimilarNames ? validationResult?.values : undefined
            }}
          />
          {!disabled && (
            <Button.Icon
              className="user-form_delete-btn mt-12"
              type={BUTTON_TYPES.LINK}
              icon={<Delete24 />}
              onClick={() => {
                remove(name)
              }}
              danger
            />
          )}
        </div>
      </Space>
    )
  }

  const emptyAddressState = useMemo(
    () => (
      <div className="empty-address-section flex justify-center align-center p-24 mt-24">
        <Typography.Body1
          weight={TYPOGRAPHY_WEIGHT.BOLD}
          className="empty-address-section__text"
        >
          No added addresses
        </Typography.Body1>
      </div>
    ),
    []
  )

  const returnRemitToAddress = (
    key,
    name,
    _restField,
    remove,
    _numberInQueue,
    isRemit,
    isDuplicate
  ) => {
    const addressSetName = isRemit
      ? 'remit_to_address_set'
      : 'order_from_address_set'

    return (
      <Space
        key={`${key}-${isRemit ? 'remit-address' : 'order-address'}-${name}`}
        className="user-form_input-wide hospitals-wrapper"
        direction="vertical"
      >
        <div className={cn('column full-width align-end mt-24')}>
          <div className="row gap-24 align-center space-between">
            <div
              className={cn('user-form_title', {
                'error-label': isDuplicate
              })}
            >
              {isRemit ? 'Remit to address' : 'Order from address'} {name + 1}
            </div>
            {!disabled && (
              <Button.Icon
                className="user-form_delete-btn"
                type={BUTTON_TYPES.LINK}
                icon={<Delete24 />}
                onClick={() => {
                  remove(name)
                }}
                danger
              />
            )}
          </div>
          {isDuplicate && (
            <Banner.Error
              text={'Duplicate address not allowed.'}
              className="mt-12"
            />
          )}
          <VendorAddress
            form={form}
            namePath={[name]}
            formPath={[addressSetName, name]}
            errorPath={[addressSetName, name]}
            disabled={disabled}
            isDuplicate={isDuplicate}
            streetClassName="full-width"
            className="full-width"
            getVendorError={getVendorError}
          />
        </div>
      </Space>
    )
  }

  return (
    <div className="vendor-details-form">
      <div className="vendor-details-form__grid-column">
        <RowLayout title="Access type">{VendorPermissionsField}</RowLayout>
        <div className="vendor-details-form__divider" />
        <RowLayout title="Business information">
          <div className="column gap-24">
            <div className="vendor-details-form__grid-row two-columns">
              {NameField}
              {LegalNameField}
            </div>
            <Form.List name="alternate_name_set">
              {(fields, { add, remove }) => {
                // Group fields into rows of 2
                const rows: typeof fields[] = []
                for (let i = 0; i < fields.length; i += 2) {
                  rows.push(fields.slice(i, i + 2))
                }

                return (
                  <>
                    <Typography.Headline6>
                      Vendor alternate name
                    </Typography.Headline6>
                    <div className="column">
                      {rows.map((row, rowIndex) => (
                        <div key={`row-${rowIndex}`} className="row gap-24">
                          {row.map(({ key, name }, fieldIndex) => {
                            const originalIndex = rowIndex * 2 + fieldIndex
                            return (
                              <div key={key} className="full-width">
                                {returnAlternateName(
                                  key,
                                  name,
                                  remove,
                                  duplicateAlternateIndices.has(originalIndex)
                                )}
                              </div>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                    {!disabled &&
                      (alternateNames?.length >= 10 ? (
                        <Tooltip
                          placement="topLeft"
                          title={TOOLTIP.YOU_HAVE_REACHED_LIMIT}
                        >
                          <div>
                            <Button
                              type={BUTTON_TYPES.GHOST}
                              icon={<Plus16 />}
                              className="user-form_add-hospital"
                              onClick={() => add()}
                              disabled={true}
                              upperCase
                            >
                              {BTN_TXT.ADD_ALTERNATE_NAME}
                            </Button>
                          </div>
                        </Tooltip>
                      ) : (
                        <Button
                          type={BUTTON_TYPES.GHOST}
                          icon={<Plus16 />}
                          className="user-form_add-hospital"
                          onClick={() => add()}
                          disabled={alternateNames?.length >= 10}
                          upperCase
                        >
                          {BTN_TXT.ADD_ALTERNATE_NAME}
                        </Button>
                      ))}
                  </>
                )
              }}
            </Form.List>
            <div className="vendor-details-form__grid-row two-columns">
              {BusinessTypeField}
              {PhoneNumberField}
            </div>
            {DescriptionField}
          </div>
        </RowLayout>
        <div className="vendor-details-form__divider" />
        <RowLayout title="Addresses">
          <Typography.Headline6>Legal address</Typography.Headline6>
          <VendorAddress
            form={form}
            namePath={['vendor', 'address']}
            errorPath={['address']}
            disabled={disabled}
            allowClearState
            getVendorError={getVendorError}
          />
          <Typography.Headline6 className="mt-24">
            Remit to address
          </Typography.Headline6>
          <Form.List name={['remit_to_address_set']}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, id) =>
                  returnRemitToAddress(
                    key,
                    name,
                    restField,
                    remove,
                    id,
                    true,
                    duplicateIndicesRemit.has(name)
                  )
                )}
                {!fields.length && disabled && emptyAddressState}
                {!disabled &&
                  (remitToAddresses?.length >= 10 ? (
                    <Tooltip
                      placement="topLeft"
                      title={TOOLTIP.YOU_HAVE_REACHED_LIMIT}
                    >
                      <div>
                        <Button
                          type={BUTTON_TYPES.GHOST}
                          icon={<Plus16 />}
                          className="user-form_add-hospital mt-24"
                          onClick={() =>
                            add({ country: COUNTRIES_LIST[0].value })
                          }
                          disabled={true}
                          upperCase
                        >
                          {BTN_TXT.ADD_REMIT_TO_ADDRESS}
                        </Button>
                      </div>
                    </Tooltip>
                  ) : (
                    <Button
                      type={BUTTON_TYPES.GHOST}
                      icon={<Plus16 />}
                      className="user-form_add-hospital mt-24"
                      onClick={() => add({ country: COUNTRIES_LIST[0].value })}
                      disabled={remitToAddresses?.length === 10}
                      upperCase
                    >
                      {BTN_TXT.ADD_REMIT_TO_ADDRESS}
                    </Button>
                  ))}
              </>
            )}
          </Form.List>
          <Typography.Headline6 className="mt-24">
            Order from address
          </Typography.Headline6>
          <Form.List name="order_from_address_set">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, id) =>
                  returnRemitToAddress(
                    key,
                    name,
                    restField,
                    remove,
                    id,
                    false,
                    duplicateIndicesOrder.has(name)
                  )
                )}
                {!fields.length && disabled && emptyAddressState}
                {!disabled &&
                  (orderFromAddress?.length >= 10 ? (
                    <Tooltip
                      placement="topLeft"
                      title={TOOLTIP.YOU_HAVE_REACHED_LIMIT}
                    >
                      <div>
                        <Button
                          type={BUTTON_TYPES.GHOST}
                          icon={<Plus16 />}
                          className="user-form_add-hospital mt-24"
                          onClick={() =>
                            add({ country: COUNTRIES_LIST[0].value })
                          }
                          disabled={true}
                          upperCase
                        >
                          {BTN_TXT.ADD_ORDER_FROM_ADDRESS}
                        </Button>
                      </div>
                    </Tooltip>
                  ) : (
                    <Button
                      type={BUTTON_TYPES.GHOST}
                      icon={<Plus16 />}
                      className="user-form_add-hospital mt-24"
                      onClick={() => add({ country: COUNTRIES_LIST[0].value })}
                      disabled={orderFromAddress?.length === 10}
                      upperCase
                    >
                      {BTN_TXT.ADD_ORDER_FROM_ADDRESS}
                    </Button>
                  ))}
              </>
            )}
          </Form.List>
        </RowLayout>
      </div>
    </div>
  )
}

export default VendorForm
